
// import { useDataUtils } from '@/hooks/useDataUtils'; // Removed unused import
import { useGoogleBusinessSearch } from '@/hooks/useGoogleBusinessSearch';
import { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';

import { BusinessDetails } from '../../addBusinessModal/types';
import { MockGooglePlace } from '@/utils/mockGoogleMapsApi';

export const useBusinessSelection = (
  setBusinessDetails: React.Dispatch<React.SetStateAction<BusinessDetails>>,
  setSelectedDate: React.Dispatch<React.SetStateAction<Date | undefined>>,
  setFormSection: React.Dispatch<React.SetStateAction<'search' | 'form'>>
) => {
  const { getBusinessDetails } = useGoogleBusinessSearch();
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Helper function to determine default timezone based on state
  const getDefaultTimezone = (stateCode: string): string => {
    switch(stateCode) {
      case 'ME': case 'NH': case 'VT': case 'MA': case 'RI': case 'CT': 
      case 'NY': case 'NJ': case 'DE': case 'MD': case 'DC': case 'VA': 
      case 'NC': case 'SC': case 'GA': case 'FL': case 'PA': case 'WV':
      case 'OH': // Moved OH to Eastern
        return 'Eastern Standard Time';
      case 'MI': case 'IN': case 'KY': case 'TN': case 'AL': 
      case 'MS': case 'IL': case 'MO': case 'AR': case 'LA': case 'WI': 
      case 'MN': case 'IA':
        return 'Central Standard Time';
      case 'ND': case 'SD': case 'NE': case 'KS': case 'OK': 
      case 'TX': case 'MT': case 'WY': case 'CO': case 'NM':
        return 'Mountain Standard Time';
      // Arizona does not observe DST, except for Navajo Nation.
      // For simplicity, often mapped to Mountain Standard Time without DST.
      // Or, if more precision is needed, a library might be better.
      // Google Places API itself can return timezone info if requested.
      case 'AZ': 
        return 'Mountain Standard Time (no DST)'; // Or 'Arizona Standard Time'
      case 'ID': case 'UT': case 'NV': case 'CA': case 'OR': case 'WA':
        return 'Pacific Standard Time';
      case 'AK':
        return 'Alaska Standard Time';
      case 'HI':
        return 'Hawaii Standard Time';
      case 'PR': case 'VI':
        return 'Puerto Rico and US Virgin Islands Time';
      case 'GU': case 'MP':
        return 'Chamorro Standard Time';
      case 'AS':
        return 'Samoa Standard Time';
      default:
        return 'Eastern Standard Time'; // Default
    }
  };

  const handleBusinessSelect = async (business: MockGooglePlace) => {
    if (!business || !business.place_id) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Get detailed information about the selected business
      const details = await getBusinessDetails(business.place_id);
      
      if (details) {
        console.log("useBusinessSelection - Received details from getBusinessDetails:", details); // Log received details
        // Convert from latitude/longitude to lat/lng format
        // details.coordinates should already be in { latitude, longitude } format from googlePlacesProxy
        const apiCoordinates = details.coordinates; // { latitude, longitude }

        // Determine default timezone based on state
        const stateFromDetails = details.state || '';
        console.log("useBusinessSelection - State from details for timezone:", stateFromDetails);
        const timeZone = stateFromDetails ? getDefaultTimezone(stateFromDetails) : '';
        
        // Clean the website URL
        let websiteUrl = details.website || '';
        if (websiteUrl.endsWith(',')) {
          websiteUrl = websiteUrl.slice(0, -1);
        }

        // Process the business name for shortName
        const businessName = details.name || '';
        const shortName = businessName.length > 30 ? businessName.substring(0, 30) : businessName;
        
        // Create full business details object with all properties
        const businessData = {
          name: businessName,
          shortName: shortName,
          phone: details.phone || '',
          email: details.email || '',
          industry: details.industry || '',
          industryVertical: '',
          address: details.address || '',
          city: details.city || '',
          state: details.state || '',
          zip: details.zip || '',
          website: websiteUrl, // Use cleaned URL
          founded: details.founded || '',
          // Set the timezone and ensure it's not overwritten later
          timeZone: timeZone,
          coordinates: apiCoordinates // Use the renamed variable
        };
        
        // Update business details with all fields at once to avoid overwrites
        setBusinessDetails(businessData);
        
        if (details.founded) {
          const foundedYear = parseInt(details.founded, 10);
          if (!isNaN(foundedYear)) {
            setSelectedDate(new Date(foundedYear, 0, 1));
          }
        }
        
        // Explicitly transition to form section
        console.log("useBusinessSelection: Setting formSection to 'form'");
        setFormSection('form');
        
        // Show toast about timezone setting if not on mobile
        if (!isMobile && timeZone) {
          toast({
            title: "Timezone set",
            description: `Timezone automatically set to ${timeZone} based on business location.`,
          });
        }
      } else {
        // Process the business name for shortName
        const businessName = business.name || '';
        const shortName = businessName.length > 30 ? businessName.substring(0, 30) : businessName;
        
        // Create minimal business info from the search result with default values for required fields
        setBusinessDetails({
          name: businessName,
          shortName: shortName,
          address: business.formatted_address || business.vicinity || '',
          phone: '',
          email: '',
          industry: '',
          industryVertical: '',
          city: '',
          state: '',
          zip: '',
          website: '',
          timeZone: '',
          coordinates: null,
        });
        
        // Explicitly transition to form section
        console.log("useBusinessSelection: Setting formSection to 'form' (minimal details)");
        setFormSection('form');
      }
    } catch (error) {
      console.error("Error selecting business:", error);
      
      // Only show error toast when not on mobile
      if (!isMobile) {
        toast({
          title: "Error",
          description: "Could not retrieve business details. Please try creating a new business instead.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCreateNew = (businessName: string) => {
    console.log("useBusinessSelection: handleCreateNew called with name:", businessName);
    
    // Process the business name for shortName
    const shortName = businessName.length > 30 ? businessName.substring(0, 30) : businessName;
    
    // Update business details with the new name, ensuring we keep all existing values
    setBusinessDetails(prev => {
      // If prev is undefined or null, create a new object with default values
      const defaultValues = {
        name: businessName,
        shortName: shortName,
        address: '',
        phone: '',
        email: '',
        industry: '',
        industryVertical: '',
        city: '',
        state: '',
        zip: '',
        website: '',
        timeZone: '',
        coordinates: null,
      };
      
      // Return the merged object
      return {
        ...defaultValues,
        ...prev,
        name: businessName,
        shortName: shortName
      };
    });
    
    // Explicitly transition to form section
    console.log("useBusinessSelection: Setting formSection to 'form' (create new)");
    setFormSection('form');
  };

  return {
    handleBusinessSelect,
    handleCreateNew,
    isLoading
  };
};
